import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { Field } from '../../types/field';
import type {
  ExtendedSummary,
  PricingTableColumn,
  PricingTableRow,
  PricingTableSection,
  PricingTableSectionSummaryValues,
  PricingTableSummaryValues,
  TaxBreakdown,
} from '../../types/pricing-table';
import type { PricingTable } from '../../types/models';
import {
  ensureSectionSummaryValues,
  ensureSummaryValues,
  getSectionsWithRowsFromInputValues,
} from './editor-node-pricing-table.helper';
import * as PricingTableHelper from './pricing-table.helper';

export function calculateCombinedSubTotal(sections: PricingTableSection[]): string {
  return sections
    .map(section => {
      const { price } = calculateSectionSummary(
        section.rows,
        section.columns,
        section.sectionSummary
      );
      return Number(price.value);
    })
    .reduce((a, b) => a + b, 0)
    .toString();
}

export function calculateBaseSubtotal(sections: PricingTableSection[]): string {
  return sections
    .map(section => {
      const { price, tax } = calculateSectionSummary(
        section.rows,
        section.columns,
        section.sectionSummary
      );
      return Number(price.value) - Number(tax.value);
    })
    .reduce((a, b) => a + b, 0)
    .toString();
}

export function calculateTotalPrice(
  summaryValues: PricingTableSummaryValues,
  sections: PricingTableSection[]
): string {
  const subtotal = calculateCombinedSubTotal(sections);
  const {
    value: discountValue,
    flatFee: discountFlatFee,
    enabled: discountEnabled,
  } = summaryValues.discount;
  const { value: taxValue, flatFee: taxFlatFee, enabled: taxEnabled } = summaryValues.tax;

  const priceAfterDiscount = discountEnabled
    ? PricingTableHelper.getPriceAfterDiscount(discountFlatFee, discountValue, subtotal)
    : Number(subtotal);

  const priceAfterDiscountAndTax = taxEnabled
    ? PricingTableHelper.getPriceAfterTax(taxFlatFee, priceAfterDiscount, taxValue)
    : Number(priceAfterDiscount);

  return String(priceAfterDiscountAndTax);
}

export function calculateSectionSummary(
  rows: PricingTableRow[],
  columns: PricingTableColumn[],
  sectionSummary: PricingTableSectionSummaryValues
): PricingTableSectionSummaryValues {
  const subtotalDiscount = String(
    rows
      .map(row => PricingTableHelper.calculateRowDiscountSum(row, columns))
      .reduce((a, b) => a + b, 0)
  );

  const subtotalTax = String(
    rows.map(row => PricingTableHelper.calculateRowTaxSum(row, columns)).reduce((a, b) => a + b, 0)
  );
  const rowsTotal = rows
    .map(row => Number(PricingTableHelper.calculateRowTotalPrice(row, columns)?.value || 0))
    .reduce((a, b) => a + b, 0);
  const subtotal = String(rowsTotal - Number(subtotalDiscount) + Number(subtotalTax));

  const extendedSummaries = (sectionSummary?.extendedSummaries || []).map(summary => {
    const isTotalPriceSummary =
      summary.columnId === PricingTableHelper.getColumnTotalPriceId(columns);

    const columnTotal = isTotalPriceSummary
      ? rowsTotal
      : rows
          .map(row =>
            Number(row.values?.find(({ columnId }) => columnId === summary.columnId)?.value || 0)
          )
          .reduce((a, b) => a + b, 0);

    return {
      ...summary,
      value: String(columnTotal),
    };
  }, {} as ExtendedSummary);

  return {
    displayName: '',
    ...sectionSummary,
    price: {
      ...sectionSummary.price,
      value: subtotal,
    },
    discount: {
      ...sectionSummary.discount,
      value: subtotalDiscount,
    },
    tax: {
      ...sectionSummary.tax,
      value: subtotalTax,
    },
    extendedSummaries,
  };
}

export function calculateSummaryValues(
  pricingTable: PricingTable,
  fields: Field[]
): PricingTableSummaryValues {
  const sections = getSectionsWithRowsFromInputValues(pricingTable, fields);
  const summary = ensureSummaryValues(pricingTable.pricingTableSummaryValues);

  const totalPriceValue = calculateTotalPrice(summary, sections);
  const combinedSubtotalValue = calculateCombinedSubTotal(sections);
  const baseSubtotalValue = calculateBaseSubtotal(sections);
  const taxBreakdown = calculateTaxBreakdown(pricingTable, fields);

  return {
    displayName: summary.displayName,
    discount: {
      ...summary.discount,
    },
    tax: {
      ...summary.tax,
    },
    price: {
      ...summary.price,
      value: totalPriceValue,
    },
    baseSubtotal: {
      ...summary.baseSubtotal,
      value: baseSubtotalValue,
    },
    taxBreakdown: {
      ...taxBreakdown,
      rates: taxBreakdown.rates.sort((a, b) => a.rate - b.rate),
    },
    combinedSubtotal: {
      ...summary.combinedSubtotal,
      value: combinedSubtotalValue,
    },
  };
}

export function calculateTaxBreakdown(pricingTable: PricingTable, fields: Field[]): TaxBreakdown {
  const { taxBreakdown } = ensureSummaryValues(pricingTable.pricingTableSummaryValues);
  const taxRateMap = getTaxRateMap(pricingTable.pricingTableSections, fields);
  const calculatedRates = taxBreakdown.rates.map(rate => {
    const rateValue = taxRateMap.get(rate.rate) ?? 0;
    return { ...rate, value: String(rateValue) };
  });

  const taxFlatFeeTotal = getTaxFlatFeeTotal(pricingTable, fields);
  const taxTotal =
    calculatedRates.reduce((acc, rate) => acc + Number(rate.value), 0) + taxFlatFeeTotal;

  return {
    ...taxBreakdown,
    value: String(taxTotal),
    rates: calculatedRates,
  };
}

export function getArrayDiff(
  arr1: number[],
  arr2: number[]
): { toAdd: number[]; toRemove: number[] } {
  const set1 = new Set(arr1);
  const set2 = new Set(arr2);

  const toAdd = [...set1].filter(num => !set2.has(num));
  const toRemove = [...set2].filter(num => !set1.has(num));

  return { toAdd, toRemove };
}

export function getTaxRatesDifferences(pricingTable: PricingTable, fields: Field[]) {
  const summaryTaxRates = ensureSummaryValues(
    pricingTable.pricingTableSummaryValues
  ).taxBreakdown.rates.map(rate => rate.rate);

  const taxRateMap = getTaxRateMap(pricingTable.pricingTableSections, fields);
  const calculatedTaxRates = [...taxRateMap.keys()];
  const { toAdd, toRemove } = getArrayDiff(calculatedTaxRates, summaryTaxRates);

  return { toAdd, toRemove };
}

export function getSectionSummary(
  section: PricingTableSection,
  fields: Field[],
  isPreCalculated: boolean
) {
  if (isPreCalculated) {
    return ensureSectionSummaryValues(section.sectionSummary);
  }

  const rowsFromInputFieldValues = PricingTableHelper.getRowsFromInputFieldValues(
    PricingTableHelper.getEnabledRows(section.rows, fields),
    PricingTableHelper.getEnabledColumns(section.columns),
    fields
  );

  const calculatedValues = calculateSectionSummary(
    rowsFromInputFieldValues,
    section.columns,
    section.sectionSummary
  );

  return ensureSectionSummaryValues(calculatedValues);
}

export function getSectionTaxRateMap(
  section: PricingTableSection,
  fields: Field[] = []
): Map<number, number> {
  const enabledColumns = PricingTableHelper.getEnabledColumns(section.columns);
  const rowsFromInputFieldValues = PricingTableHelper.getRowsFromInputFieldValues(
    section.rows,
    enabledColumns,
    fields
  );
  const taxColumnId = PricingTableHelper.getColumnTaxId(enabledColumns);

  return rowsFromInputFieldValues.reduce((taxRates, row) => {
    const taxRate = parseFloat(
      row.values.find(({ columnId }) => columnId === taxColumnId)?.value || '0'
    );
    if (!isNaN(taxRate) && taxRate !== 0 && !row.taxFlatFee) {
      const currentSum = taxRates.get(taxRate) || 0;
      const rowSum = PricingTableHelper.getIsRowEnabled(row, fields)
        ? PricingTableHelper.calculateRowTaxSum(row, section.columns)
        : 0;
      const newSum = currentSum + rowSum;
      taxRates.set(taxRate, newSum);
    }
    return taxRates;
  }, new Map<number, number>());
}

export function getTaxRateMap(sections: PricingTableSection[], fields: Field[] = []) {
  return sections.reduce((acc, section) => {
    const sectionTaxMap = getSectionTaxRateMap(section, fields);
    sectionTaxMap.forEach((sum, taxRate) => {
      acc.set(taxRate, (acc.get(taxRate) || 0) + sum);
    });
    return acc;
  }, new Map<number, number>());
}

export function getUpdatedTaxBreakDown(pricingTable: PricingTable, fields: Field[]): TaxBreakdown {
  const { taxBreakdown } = ensureSummaryValues(pricingTable.pricingTableSummaryValues);
  const { toAdd, toRemove } = getTaxRatesDifferences(pricingTable, fields);

  const updatedRates = taxBreakdown.rates.filter(rate => !toRemove.includes(rate.rate));

  toAdd.forEach(rate => {
    if (!Number.isInteger(rate)) {
      return;
    }

    updatedRates.push({
      displayName: `${t('tax')} ${rate}%`,
      enabled: true,
      rate,
      value: '',
    });
  });

  return {
    ...taxBreakdown,
    rates: updatedRates,
  };
}

export function getTaxFlatFeeTotal(pricingTable: PricingTable, fields: Field[]): number {
  return pricingTable.pricingTableSections.reduce((grandTotal, section) => {
    const enabledColumns = PricingTableHelper.getEnabledColumns(section.columns);
    const taxColumnId = PricingTableHelper.getColumnTaxId(enabledColumns);
    const rowsFromInputFieldValues = PricingTableHelper.getRowsFromInputFieldValues(
      section.rows,
      enabledColumns,
      fields
    );

    const rowTotal = rowsFromInputFieldValues.reduce((totalFlatFeeTax, row) => {
      const isRowEnabled = PricingTableHelper.getIsRowEnabled(row, fields);
      const taxValue = parseFloat(
        row.values.find(({ columnId }) => columnId === taxColumnId)?.value || '0'
      );
      const isValidTaxValue = isRowEnabled && row.taxFlatFee && !isNaN(taxValue);
      return isValidTaxValue ? totalFlatFeeTax + taxValue : totalFlatFeeTax;
    }, 0);
    return grandTotal + rowTotal;
  }, 0);
}
